import React, { useState } from 'react';
import { X, Download, FileText, FileSpreadsheet, File, Database, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';
import './UserExportModal.scss';

const UserExportModal = ({ isOpen, onClose, users, filteredUsers }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState('excel');
  const [exportScope, setExportScope] = useState('filtered'); // 'all' or 'filtered'
  const [customFileName, setCustomFileName] = useState('');

  if (!isOpen) return null;

  const dataToExport = exportScope === 'all' ? users : filteredUsers;
  const stats = {
    totalUsers: dataToExport.length,
    withDoterraAccount: dataToExport.filter(u => u.hasDoterraAccount === 'yes').length,
    withoutDoterraAccount: dataToExport.filter(u => u.hasDoterraAccount === 'no').length,
    withPhone: dataToExport.filter(u => u.phoneNumber && u.phoneNumber.length > 0).length,
    activeUsers: dataToExport.filter(u => u.status === 'active' || !u.status).length,
    inactiveUsers: dataToExport.filter(u => u.status === 'inactive').length,
    recentUsers: dataToExport.filter(u => {
      if (!u.createdAt) return false;
      const createdDate = new Date(u.createdAt.seconds * 1000);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return createdDate > thirtyDaysAgo;
    }).length,
    thisWeek: dataToExport.filter(u => {
      if (!u.createdAt) return false;
      const createdDate = new Date(u.createdAt.seconds * 1000);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return createdDate > sevenDaysAgo;
    }).length,
    campaigns: [...new Set(dataToExport.map(u => u.campaignId).filter(Boolean))].length
  };

  const exportToExcel = async (data, filename) => {
    try {
      // Create CSV content for Excel compatibility
      const headers = [
        'ID', 'Email', 'Nume', 'Prenume', 'Telefon', 'DoTERRA ID', 'DoTERRA Nume', 
        'Mentor', 'Mentor ID', 'Status', 'Data Creării', 'Data Ultimei Actualizări'
      ];
      const csvContent = [
        headers.join(','),
        ...data.map(user => [
          user.id || '',
          user.email || '',
          user.firstName || '',
          user.lastName || '',
          user.phoneNumber || '',
          user.doterraId || '',
          user.doterraFullName || '',
          user.mentorFullName || '',
          user.mentorDoterraId || '',
          user.hasDoterraAccount === 'yes' ? 'Cu cont doTERRA' : 'Fără cont doTERRA',
          user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('ro-RO') : '',
          user.updatedAt ? new Date(user.updatedAt.seconds * 1000).toLocaleDateString('ro-RO') : ''
        ].map(field => `"${field}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      return { success: true, message: 'Export Excel realizat cu succes!' };
    } catch (error) {
      console.error('Export error:', error);
      return { success: false, message: 'Eroare la exportul Excel!' };
    }
  };

  const exportToCSV = async (data, filename) => {
    try {
      const headers = [
        'ID', 'Email', 'Nume', 'Prenume', 'Telefon', 'DoTERRA ID', 'DoTERRA Nume', 
        'Mentor', 'Mentor ID', 'Status', 'Data Creării', 'Data Ultimei Actualizări'
      ];
      const csvContent = [
        headers.join(','),
        ...data.map(user => [
          user.id || '',
          user.email || '',
          user.firstName || '',
          user.lastName || '',
          user.phoneNumber || '',
          user.doterraId || '',
          user.doterraFullName || '',
          user.mentorFullName || '',
          user.mentorDoterraId || '',
          user.hasDoterraAccount === 'yes' ? 'Cu cont doTERRA' : 'Fără cont doTERRA',
          user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('ro-RO') : '',
          user.updatedAt ? new Date(user.updatedAt.seconds * 1000).toLocaleDateString('ro-RO') : ''
        ].map(field => `"${field}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      return { success: true, message: 'Export CSV realizat cu succes!' };
    } catch (error) {
      console.error('Export error:', error);
      return { success: false, message: 'Eroare la exportul CSV!' };
    }
  };

  const exportToPDF = async (data, filename) => {
    try {
      // Import jsPDF and autoTable if not already imported
      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');
      
      // Create new PDF document
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      
      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Raport Utilizatori - The Oils Babe', 20, 20);
      
      // Add export date and stats
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Exportat la: ${new Date().toLocaleDateString('ro-RO')} ${new Date().toLocaleTimeString('ro-RO')}`, 20, 30);
      doc.text(`Total utilizatori: ${data.length}`, 20, 35);
      doc.text(`Cu cont doTERRA: ${data.filter(u => u.hasDoterraAccount === 'yes').length}`, 20, 40);
      doc.text(`Fara cont doTERRA: ${data.filter(u => u.hasDoterraAccount === 'no').length}`, 20, 45);
      
      // Prepare table data
      const tableColumns = [
        'ID', 'Email', 'Nume', 'Prenume', 'Telefon', 'DoTERRA ID', 'Status', 'Data Crearii'
      ];
      
      const tableRows = data.map(user => [
        user.id || '',
        user.email || '',
        user.firstName || '',
        user.lastName || '',
        user.phoneNumber || '',
        user.doterraId || '',
        user.hasDoterraAccount === 'yes' ? 'Cu cont doTERRA' : 'Fara cont doTERRA',
        user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('ro-RO') : ''
      ]);
      
      // Add table using jsPDF autoTable
      doc.autoTable({
        head: [tableColumns],
        body: tableRows,
        startY: 55,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [144, 77, 113], // Theme color
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 15 },  // ID
          1: { cellWidth: 50 },  // Email
          2: { cellWidth: 25 },  // Nume
          3: { cellWidth: 25 },  // Prenume
          4: { cellWidth: 30 },  // Telefon
          5: { cellWidth: 25 },  // DoTERRA ID
          6: { cellWidth: 35 },  // Status
          7: { cellWidth: 30 }   // Data Crearii
        },
        margin: { top: 20, left: 20, right: 20 },
        theme: 'striped'
      });
      
      // Save the PDF
      const timestamp = new Date().toISOString().slice(0, 10);
      doc.save(`${filename}_${timestamp}.pdf`);
      
      return { success: true, message: 'Export PDF realizat cu succes!' };
    } catch (error) {
      console.error('Export error:', error);
      return { success: false, message: 'Eroare la exportul PDF!' };
    }
  };

  const exportToJSON = async (data, filename) => {
    try {
      const jsonContent = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.json`;
      link.click();
      URL.revokeObjectURL(link.href);

      return { success: true, message: 'Export JSON realizat cu succes!' };
    } catch (error) {
      console.error('Export error:', error);
      return { success: false, message: 'Eroare la exportul JSON!' };
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const filename = customFileName.trim() || 'users_export';
      let result;

      switch (exportType) {
        case 'excel':
          result = await exportToExcel(dataToExport, filename);
          break;
        case 'csv':
          result = await exportToCSV(dataToExport, filename);
          break;
        case 'pdf':
          result = await exportToPDF(dataToExport, filename);
          break;
        case 'json':
          result = await exportToJSON(dataToExport, filename);
          break;
        default:
          result = { success: false, message: 'Format de export necunoscut!' };
      }

      if (result.success) {
        toast.success(result.message);
        onClose();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la exportul datelor!');
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    {
      id: 'excel',
      name: 'Excel (XLSX)',
      description: 'Ideal pentru analiză și editare în Excel',
      detailedDescription: 'Perfect pentru analize complexe, grafice și editare avansată. Include formatare și structură de date optimizată.',
      icon: <FileSpreadsheet size={20} />,
      color: '#217346'
    },
    {
      id: 'csv',
      name: 'CSV',
      description: 'Format universal, compatibil cu multe aplicații',
      detailedDescription: 'Format simplu și universal, compatibil cu Excel, Google Sheets, și majoritatea aplicațiilor de date.',
      icon: <FileText size={20} />,
      color: '#0078d4'
    },
    {
      id: 'pdf',
      name: 'PDF',
      description: 'Perfect pentru rapoarte și prezentări',
      detailedDescription: 'Ideal pentru distribuire, arhivare și prezentări. Păstrează formatarea și nu poate fi modificat.',
      icon: <File size={20} />,
      color: '#d83b01'
    },
    {
      id: 'json',
      name: 'JSON (Backup)',
      description: 'Backup complet pentru restaurare ulterioară',
      detailedDescription: 'Backup complet cu toate datele și metadata. Perfect pentru migrarea sau restaurarea sistemului.',
      icon: <Database size={20} />,
      color: '#5c2d91'
    }
  ];

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <div className="header-content">
            <h2>
              <Download size={24} />
              Export Date Utilizatori
            </h2>
            <p>Exportă datele în formatul dorit</p>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="export-modal-content">
          {/* Statistics */}
          <div className="export-stats">
            <h3>
              <BarChart3 size={20} />
              Statistici Export
            </h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Utilizatori</div>
                <div className="stat-value">{stats.totalUsers}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Cu cont doTERRA</div>
                <div className="stat-value enrolled">{stats.withDoterraAccount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Fără cont doTERRA</div>
                <div className="stat-value challenges">{stats.withoutDoterraAccount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Cu telefon</div>
                <div className="stat-value samples">{stats.withPhone}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Utilizatori activi</div>
                <div className="stat-value active">{stats.activeUsers}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Utilizatori inactivi</div>
                <div className="stat-value inactive">{stats.inactiveUsers}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Înregistrați recent (30 zile)</div>
                <div className="stat-value campaigns">{stats.recentUsers}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Înregistrați săptămâna aceasta</div>
                <div className="stat-value week">{stats.thisWeek}</div>
              </div>
            </div>
          </div>

          {/* Export Scope */}
          <div className="export-section">
            <h3>Date de exportat:</h3>
            <div className="scope-options">
              <div className="scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="filtered"
                  checked={exportScope === 'filtered'}
                  onChange={(e) => setExportScope(e.target.value)}
                  id="scope-filtered"
                />
                <label htmlFor="scope-filtered">
                  <div className="scope-title">Date filtrate</div>
                  <div className="scope-count">({filteredUsers.length} utilizatori)</div>
                </label>
              </div>
              <div className="scope-option">
                <input
                  type="radio"
                  name="exportScope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={(e) => setExportScope(e.target.value)}
                  id="scope-all"
                />
                <label htmlFor="scope-all">
                  <div className="scope-title">Toate datele</div>
                  <div className="scope-count">({users.length} utilizatori)</div>
                </label>
              </div>
            </div>
          </div>

          {/* Export Format */}
          <div className="export-section">
            <h3>Format de export:</h3>
            <div className="export-options">
              {exportOptions.map(option => (
                <div key={option.id} className="export-option">
                  <input
                    type="radio"
                    name="exportType"
                    value={option.id}
                    checked={exportType === option.id}
                    onChange={(e) => setExportType(e.target.value)}
                    id={`format-${option.id}`}
                  />
                  <label htmlFor={`format-${option.id}`}>
                    <div className="option-header">
                      <div className={`option-icon ${option.id}`}>
                        {option.icon}
                      </div>
                      <h4 className="option-title">{option.name}</h4>
                    </div>
                    <p className="option-description">{option.description}</p>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Filename */}
          <div className="export-section">
            <div className="filename-input">
              <div className="input-group">
                <label htmlFor="filename">Nume fișier (opțional):</label>
                <input
                  type="text"
                  id="filename"
                  placeholder="users_export"
                  value={customFileName}
                  onChange={(e) => setCustomFileName(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="export-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Anulează
          </button>
          <button 
            className="export-button"
            onClick={handleExport}
            disabled={isExporting}
          >
            <Download size={18} />
            {isExporting ? 'Se exportă...' : `Exportă ${exportType.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserExportModal;
